/* eslint-disable no-unused-expressions */
/**
 * @file JS file for the header section
 * @author Ray M., Aug 2019
 */

// ? [REVISIT] how much of this file still needs to stay
import { debounce, isEmpty } from 'lodash-es';
import { overrideInternationalShippingBanner, updateGeotargetedElementDisplay } from '../../utils/dom';
import { tryCatchWrapper } from 'src/scripts/utils/helpers';
import { contentLoaded } from '../../utils/document-promises';
import { closeSearch, openSearch } from './Search/state';
import { InitMobileTopLoyaltyPanel } from './Cart/components/Loyalty/LoyaltyTopPanel';
import { InitQRCodeAccountHeader } from './Account/QRCodeAccountHeader';
import { initSignInModal } from '../../templates/_shared/ui-components/SignInModal';
import { initErrorSnackbar } from '../../templates/_shared/ui-components/ErrorSnackbar';
import { attachSignInModalOpenerToElements } from '../../globals';
import { RotateUniversalBanner } from '../../utils/rotateUniversalBanner';
import { InitSanctuaryNavDropdown } from './SanctuaryNavDropdown';

const runHeaderScripts = async () => {
  await contentLoaded;
  const isCustomer = window.getWishlistSettings?.().isCustomer;
  if (!isCustomer) initSignInModal();
  attachSignInModalOpenerToElements();
  await tryCatchWrapper(() =>
    updateGeotargetedElementDisplay({
      selector: '.uni-banner__text[unibanner_geotarget_is_enabled=true]',
    })
  );

  const isAccountPage = window.location.pathname.includes('/account');
  const isOrderDetailsPage = window.location.pathname.includes('/account/orders/');
  const urlParams = new URLSearchParams(window.location.search);
  const viewParam = urlParams.get('view');
  const shouldShowQRCodeHeader = isAccountPage && viewParam !== 'wishlist' && !isOrderDetailsPage;

  if (shouldShowQRCodeHeader) {
    InitQRCodeAccountHeader();
  } else {
    InitMobileTopLoyaltyPanel();
  }

  InitSanctuaryNavDropdown();
  initHoverEvents();
  adjustBannerHeight();
  rotateBanners();
  initSearchListeners();
  overrideInternationalShippingBanner();
  initErrorSnackbar();
};

// Hover state for the top level nav(Shop)
//@ts-expect-error // ? [REVISIT]
function handleHover(e) {
  const elements = document.querySelectorAll('.hover-target');
  if (e.type === 'mouseover') {
    if (e.currentTarget.classList.contains('hover-target')) {
      e.currentTarget.classList.add('hovered');
    } else {
      e.currentTarget.closest('li').firstElementChild.classList.add('hovered');
    }
    elements.forEach((elem) => {
      if (!elem.classList.contains('hovered')) {
        elem.style.opacity = '0.4';
      }
    });
  } else if (e.type === 'mouseleave') {
    if (e.currentTarget.classList.contains('hover-target')) {
      e.currentTarget.classList.remove('hovered');
    } else {
      e.currentTarget.closest('li').firstElementChild.classList.remove('hovered');
    }
    elements.forEach((elem) => {
      if (!elem.classList.contains('hovered')) {
        elem.style.opacity = '1';
      }
    });
  }
}

// Hover issue workaround
let timerOpen: NodeJS.Timeout | null;
let timerClose: NodeJS.Timeout | null;

//@ts-expect-error // ? [REVISIT]
function clearEvents(e) {
  timerClose && clearTimeout(timerClose);
  timerClose = null;
  timerOpen && clearTimeout(timerOpen);
  timerOpen = null;
}

const mainSelector = document.querySelector('main');

//[REVISIT]: Everywhere where we are doing getElementById stuff to grab descendent menus ought to be just looking for a selector underneath the menuItem (the current menu item). We need fewer magic IDs.
const openSubMenu = function (e: MouseEvent) {
  const _type = null; // If running a test, check which menu to open right here.
  const menuItem = e.currentTarget as HTMLLIElement;
  if (!menuItem) {
    //something very wrong
    return null;
  }
  let name = menuItem.id;
  const lOneItems = [...document.querySelectorAll('.main-nav__item--wrapper')];
  const isAnySubMenuOpen = lOneItems.some((item) => item.classList.contains('hover'));
  const subMenu = document.getElementById(name + 'SubItem');
  mainSelector?.setAttribute('style', '--main-overlay-visibility: visible;');
  if (isAnySubMenuOpen) {
    if (timerOpen) {
      clearTimeout(timerOpen);
      timerOpen = null;
    }
    if (timerClose) {
      clearTimeout(timerClose);
      timerClose = null;
    }
    timerOpen = setTimeout(() => {
      menuItem.classList.add('hover');
      lOneItems.forEach((item) => {
        if (item !== menuItem) item.classList.remove('hover');
      });
      try {
        var type = _type || 'Control';
        if (subMenu) heap.track('Desktop Nav Open', { type: type, name: name });
      } catch (err) {
        console.log(err);
      }
    }, 300);
    if (subMenu) subMenu.addEventListener('mouseenter', clearEvents, false);
  } else {
    menuItem.classList.add('hover');
    try {
      var type = _type || 'Control';
      if (subMenu) heap.track('Desktop Nav Open', { type: type, name: name });
    } catch (err) {
      console.log(err);
    }
  }
};

const closeSubMenu = function (e: MouseEvent) {
  mainSelector?.setAttribute('style', '--main-overlay-visibility: hidden;');
  const menuItem = e.currentTarget as HTMLLIElement;
  let name = menuItem.id;
  if (timerClose) {
    clearTimeout(timerClose);
    timerClose = null;
  }
  timerClose = setTimeout(() => {
    menuItem.classList.remove('hover');
    const subMenu = document.getElementById(name + 'SubItem');
    if (subMenu) {
      subMenu.removeEventListener('mouseenter', clearEvents, false);
    }
  }, 300);
};

const sendNavDataToHeap = (e: MouseEvent) => {
  try {
    const breadcrumbs = (e.currentTarget as HTMLLIElement).dataset.nav;
    if (breadcrumbs) heap.track('Desktop Nav Click', { breadcrumbs });
  } catch (error) {
    console.log(error);
  }
};

const navItemsToTrack = Array.from(document.querySelectorAll('[data-nav]'));
if (!isEmpty(navItemsToTrack)) {
  navItemsToTrack.forEach((item) =>
    item.addEventListener('click', (e) => {
      sendNavDataToHeap(e);
    })
  );
}

/**
 *  Attach the above handlers. Note: If anyone needs to open these menus they will have to be exposed to window
 */
(() => {
  const allMenus: NodeListOf<HTMLLIElement> = document.querySelectorAll('li[data-controls-menu]');
  allMenus.forEach((el) => el.addEventListener('mouseenter', openSubMenu));
  allMenus.forEach((el) => el.addEventListener('mouseleave', closeSubMenu));
})();

// Styling of the L1 items on hover
function initHoverEvents() {
  const elements = document.querySelectorAll('.main-nav__item--wrapper');
  elements.forEach((elem) => {
    elem.addEventListener('mouseover', (event) => {
      handleHover(event);
    });
    elem.addEventListener('mouseleave', (event) => {
      handleHover(event);
    });
  });
}

const setCollapseMenuHeight = () => {
  const collapseMenu = document.getElementById('collapseMenu');
  if (collapseMenu) {
    const collapseMenuRect = collapseMenu.getBoundingClientRect();
    collapseMenu.style.height = Math.round(window.innerHeight - collapseMenuRect.top) + 'px';
  }
};

// Uni banner
//@ts-expect-error
window.toggleMobileMenu = function (event) {
  const collapseMenu = document.getElementById('collapseMenu');
  const button = document.querySelector('.navbar__toggler[data-target="#collapseMenu"]');
  const topNavMenu = document.querySelector('.top-nav__menu-list');
  const shippingInfo = document.querySelector('.top-nav__announcement-wrapper');
  if (document.body.classList.contains('mobile-nav--open')) {
    collapseMenu?.classList.remove('show');
    button?.classList.remove('active');
    document.body.classList.remove('mobile-nav--open');
    document.documentElement.classList.remove('mobile-nav--open');
    shippingInfo?.classList.add('d-flex');
    shippingInfo?.classList.remove('d-none');
    topNavMenu?.classList.remove('d-flex');
    topNavMenu?.classList.add('d-none');
    document.body.classList.remove('locked');
    if (collapseMenu) {
      collapseMenu.style.height = '0px';
    }
  } else {
    collapseMenu?.classList.add('show');
    button?.classList.add('active');
    document.body.classList.add('mobile-nav--open');
    document.documentElement.classList.add('mobile-nav--open');
    shippingInfo?.classList.remove('d-flex');
    shippingInfo?.classList.add('d-none');
    topNavMenu?.classList.add('d-flex');
    topNavMenu?.classList.remove('d-none');
    document.body.classList.add('locked');
    if (collapseMenu) {
      setCollapseMenuHeight();
    }
  }
};
//@ts-expect-error
window.toggleMobileSearch = function (event) {
  const collapseSearch = document.getElementById('collapseSearch');
  if (document.body.classList.contains('mobilesearch-nav--open')) {
    collapseSearch?.classList.remove('show');
    document.body.classList.remove('locked');
    document.body.classList.remove('mobilesearch-nav--open');
    document.documentElement.classList.remove('mobilesearch-nav--open');
    // if mob menu is open and then search is opened, close mob menu
    closeSearch();
  } else {
    collapseSearch?.classList.add('show');
    document.body.classList.add('mobilesearch-nav--open');
    document.body.classList.add('locked');
    document.documentElement.classList.add('mobilesearch-nav--open');
    // Fake searchInput focus : LND-1066 [Mweb] [Dev] Auto focus in the new search overlay is failing
    const searchField = document.getElementById('search-keyboard-up');
    if (searchField) searchField.focus();
    // end
    openSearch();
  }
};
export const closeMobileSearch = () => {
  const mobileSearchButton = document.querySelector(
    '.mobilesearch-nav--open button.navbar__toggler.mobile-search-button'
  );
  if (mobileSearchButton) {
    mobileSearchButton.click();
  }
};
//@ts-expect-error
window.closeMobileSearch = function () {
  closeMobileSearch();
};

//@ts-expect-error
window.toggleSecondLevel = function (topLevelMenuID, isOpen) {
  const firstLevel = document.querySelector('[data-target="second-level"]');
  const secondLevel = document.querySelector(`.${topLevelMenuID}`);
  if (!firstLevel || !secondLevel) return;
  if (isOpen) {
    secondLevel.classList.add('d-block');
    firstLevel.style.left = '-100%';
  } else {
    secondLevel.classList.remove('d-block');
    firstLevel.style.left = '0';
  }
};

//  Search

const watchForClickToCloseDesktopSearchDrawer = (e: MouseEvent) => {
  if (
    document.querySelector('.sign-in-modal') ||
    document.querySelector('.atc-modal') ||
    document.querySelector('.app-only-sale-atc-modal')
  )
    return;

  const form = document.getElementById('search');
  const closeButton = document.getElementById('main-navbar-search-close-icon');
  const elementClicked = e.target as HTMLElement;

  if (elementClicked === closeButton || (form && !form.contains(elementClicked))) {
    e.preventDefault();
    e.stopPropagation();
    closeDesktopSearch();
    window.removeEventListener('click', watchForClickToCloseDesktopSearchDrawer, true);
  } else {
    return;
  }
};

export const openDesktopSearch = () => {
  import('./Search').then((module) => module.initSearchForm());
  const navList = document.querySelector('.main-nav__list');
  const backdrop = document.getElementById('MainContent');
  const navSearchIcon = document.getElementById('main-navbar-search-icon');

  window.addEventListener('click', watchForClickToCloseDesktopSearchDrawer, true);
  document.addEventListener('keyup', (e) => {
    if (e.key === 'Escape') closeDesktopSearch();
  });

  navList?.classList.add('slide-out');
  if (navSearchIcon) navSearchIcon.style.display = 'none';
  backdrop?.setAttribute('style', '--main-overlay-visibility:visible;');
  openSearch();
};

export const closeDesktopSearch = () => {
  const navList = document.querySelector('.main-nav__list');
  const backdrop = document.getElementById('MainContent');
  const navSearchIcon = document.getElementById('main-navbar-search-icon');

  closeSearch();

  window.removeEventListener('click', closeDesktopSearch, true);
  document.removeEventListener('keyup', (e) => {
    if (e.key === 'Escape') closeDesktopSearch();
  });

  navList?.classList.remove('slide-out');
  if (navSearchIcon) navSearchIcon.style.display = 'block';
  backdrop?.setAttribute('style', '--main-overlay-visibility:hidden;');
};

const initSearchListeners = () => {
  const mainNavBarSearchIcon = document.getElementById('main-navbar-search-icon');
  mainNavBarSearchIcon?.addEventListener('click', openDesktopSearch);
};

//@ts-expect-error
window.toggleThirdLevel = function (e) {
  const thirdLevelMenu = document.querySelector(e.currentTarget.getAttribute('data-target'));
  if (thirdLevelMenu) {
    thirdLevelMenu.classList.toggle('collapse');
    e.currentTarget.setAttribute('aria-expanded', !thirdLevelMenu.classList.contains('collapse'));
  }
};

// Universal banners
function rotateBanners() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rotateUniversalBanner = new (RotateUniversalBanner as any)();
  rotateUniversalBanner.init();
}

// Adjust banner height depending on highest text
// ? [REVISIT] for (web vitals) CLS this should be a constant height
function adjustBannerHeight() {
  const bannerNodes = [...document.querySelectorAll('.uni-banner__text')];
  const content = bannerNodes.filter(
    (banner) => banner.style.display === 'inherit' || getComputedStyle(banner).display !== 'none'
  );
  if (!content || content.length === 0) {
    return;
  }
  const bannerContainer = document.querySelector('.uni-banner__container .col-12');
  const contentHeight = content.map((banner) => Number(getComputedStyle(banner).height.slice(0, -2)));
  const biggestHeight = contentHeight.reduce((a, b) => Math.max(a, b));
  if (!bannerContainer) return;
  if (window.innerWidth >= 768) {
    bannerContainer.style.height = `${biggestHeight + 30}px`;
  } else {
    bannerContainer.style.height = `${biggestHeight + 20}px`;
  }
}

const debouncedAdjustBannerHeight = debounce(adjustBannerHeight, 150);
window.addEventListener('resize', debouncedAdjustBannerHeight);

runHeaderScripts();
